import { useMemo, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import { AutocompleteInput } from '@/components';
import { Option } from '@/components/inputs/AutocompleteInput';
import { SelectInputOption } from '@/components/inputs/SelectInput';

interface FormOptionAutocompleteProps {
  control: Control<any>;
  name: string;
  placeholder: string;
  options: SelectInputOption[];
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

// Máximo de opciones a renderizar por búsqueda; las opciones ya vienen cargadas
// y se filtran en el front de forma incremental a medida que se escribe.
const MAX_OPTIONS = 50;

// Select con búsqueda incremental cableado a react-hook-form. Reemplaza a
// FormSelectInput cuando el listado puede ser largo (comercios, sucursales).
const FormOptionAutocomplete = ({
  control,
  name,
  placeholder,
  options,
  required = false,
  disabled = false,
  loading = false,
}: FormOptionAutocompleteProps) => {
  const { field, fieldState } = useController({ control, name });
  const [input, setInput] = useState('');

  const filtered: Option[] = useMemo(() => {
    const query = input.trim().toLowerCase();
    const list = query
      ? options.filter((option) => option.label.toLowerCase().includes(query))
      : options;
    return list
      .slice(0, MAX_OPTIONS)
      .map((option) => ({ label: option.label, data: option.value }));
  }, [options, input]);

  const selectedOption: Option | null = field.value
    ? {
        label:
          options.find((option) => option.value === field.value)?.label ?? '',
        data: field.value,
      }
    : null;

  const handleChange = (option: Option | null) => {
    field.onChange(
      option && typeof option.data === 'string' ? option.data : '',
    );
  };

  return (
    <AutocompleteInput
      name={name}
      placeholder={placeholder}
      value={selectedOption}
      onChange={handleChange}
      onInputChange={setInput}
      onBlur={field.onBlur}
      options={filtered}
      required={required}
      disabled={disabled}
      loading={loading}
      showTypingLengthHint={false}
      error={!!fieldState.error}
      errorInfo={fieldState.error?.message || ''}
    />
  );
};

export default FormOptionAutocomplete;
